import React, { useContext, useState } from "react"
import { Button, Grid } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import * as XLSX from 'xlsx'
import { useSnackbar } from "notistack"
import useNdr, { buildExtraFilters } from "./useNdr"
import ProzoDataGrid from "../../../common/ProzoDataGrid"
import DashboardSkeleton from "../DashboardSkeleton"
import WhatsAppChatPopup from "./Conversation"
import WhatsaAppContext from "./Context"
import { FormProvider, RHFTextField, RHFSelectField } from '../../../common/hook-form';
import { apiConstants, postGeneric } from "../../../common/constants"
import _ from "lodash";
import { useForm } from 'react-hook-form';
import { formatDate, getOrderStatus } from "../../../helpers/UtilityHelper";
import getRequestFilter, { NDR_TEMPLATE_KEY } from "./filter";

const yes_no = ["Yes", "No"];

export default function NDR() {
    const { dateRange } = useContext(WhatsaAppContext)
    const method = useForm({
        defaultValues: {
            awbNumber: '',
            isconversationCompleted: ''
        }
    });
    const { watch } = method
    const awbNumber = watch('awbNumber')
    const isconversationCompleted = watch('isconversationCompleted')

    const [page, setPage] = React.useState(0)
    const [sizePerPage, setSizePerPage] = React.useState(20)
    // Any filter change (or page-size change) must jump back to the first page,
    // otherwise the offset can point past the end of the filtered result set.
    React.useEffect(() => { setPage(0) }, [awbNumber, isconversationCompleted, sizePerPage])

    const filters = { awbNumber, isconversationCompleted }
    const { isLoading, groupedByAwbNumber, totalSent } = useNdr({ page, sizePerPage, filters })
    const [conversation, setConversation] = React.useState(null)
    const [downloading, setDownloading] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    if (isLoading) return <DashboardSkeleton isDashboard />

    const columnsItemModal = [
        {
            field: "awbNumber",
            headerName: "AWB Number",
            flex: 1
        },
        {
            field: "paymentMode",
            headerName: "Payment Mode",
            flex: 1
        },
        {
            field: "currentStatus",
            headerName: "Current Shipping Status",
            flex: 1
        },
        {
            field: "courierPartner",
            headerName: "Courier Partner",
            flex: 1
        },
        {
            field: "created_date",
            headerName: "Conversation Started On",
            flex: 1,
            renderCell: ({ row }) => {
                let conversation = row?.conversations[0];
                return <span>{formatDate(conversation?.created_date)}</span>
            },
            valueGetter: (params) => {
                const { row } = params;
                let conversation = row?.conversations[0];
                return formatDate(conversation?.created_date)
            },
        },
        {
            field: "isconversationCompleted",
            headerName: "Conversation Completed",
            flex: 0.5,
            renderCell: ({ row }) => {
                const completed = row?.isconversationCompleted;
                return (
                    <span style={{ fontWeight: completed ? 600 : 400 }}>
                        {completed ? 'Yes' : 'No'}
                    </span>
                );
            },
            valueGetter: (params) => {
                const { row } = params;
                return row?.isconversationCompleted ? 'Yes' : 'No'
            },
        },
        {
            field: "actionToTake",
            headerName: "Final Disposition",
            flex: 1,
            renderCell: ({ row }) => {
                let conversation = row?.conversations[0];
                return <span>{conversation?.actionToTake}</span>
            },
            valueGetter: (params) => {
                const { row } = params;
                let conversation = row?.conversations[0];
                return conversation?.actionToTake
            },
        },
        {
            field: "conversationsSummary",
            headerName: "Conversations Summary",
            type: 'number',
            flex: 1,
            renderCell: ({ row }) => {
                return <Button size="small" variant="outlined" sx={{ fontSize: 12, textTransform: 'none' }} onClick={() => { setConversation(row?.conversations[0]) }}>View Conversation</Button>
            }
        },
    ];

    // Table pagination only loads the current page — the download needs its own fetch of
    // every matching row (same date range + filters), same pattern as ndr/downloadNdr.js.
    const handleDownload = async () => {
        setDownloading(true)
        try {
            const payload = getRequestFilter(dateRange, NDR_TEMPLATE_KEY, {
                limit: 5000,
                offset: 0,
                skipCount: true,
                extraFilters: buildExtraFilters(filters),
            })
            const res = await postGeneric(apiConstants.DELIVERY_DB_SEARCH, payload)
            const rows = res?.data?.data || []
            if (!rows.length) {
                enqueueSnackbar("No records to download", { variant: "info" })
                return
            }

            const awbNumbers = _(rows).map((item) => item?.meta_data?.awbNumber || null).filter(Boolean).uniq().value()
            const shipRes = await postGeneric(apiConstants.ORDER_LISTS, {
                limit: 2147483647,
                andfilter: [{ field: 'awbNumber', operator: 'in', value: awbNumbers }],
                includes: 'id,awbNumber,orderStatus,courierDetail,paymentMode',
            })
            const allShipmentData = shipRes?.data?.data || []

            const out = rows.map((r) => {
                const shipment = _.find(allShipmentData, { awb_number: r?.meta_data?.awbNumber })
                return {
                    'AWB Number': r?.meta_data?.awbNumber || '',
                    'Payment Mode': shipment?.payment_mode || '',
                    'Current Shipping Status': shipment ? getOrderStatus(shipment.orderStatus) : '',
                    'Courier Partner': shipment?.courierDetail?.parent || shipment?.courierDetail?.name || '',
                    'Conversation Started On': r?.created_date ? formatDate(r.created_date) : '',
                    'Conversation Completed': r?.isconversationCompleted ? 'Yes' : 'No',
                    'Final Disposition': r?.actionToTake || '',
                }
            })
            const ws = XLSX.utils.json_to_sheet(out)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'WhatsApp Conversations')
            XLSX.writeFile(wb, `WhatsApp_NDR_Conversations_${new Date().toISOString().slice(0, 10)}.xlsx`)
        } catch (e) {
            enqueueSnackbar("Download failed, please try again", { variant: "error" })
        } finally {
            setDownloading(false)
        }
    }

    return (
        <React.Fragment>
            <Grid container spacing={2} backgroundColor="white">
                <Grid item xs={12}>
                    <FormProvider methods={method}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={3} >
                                <RHFTextField name="awbNumber" label="AWB" color="secondary" variant="filled" />
                            </Grid>
                            <Grid item xs={3} >
                                <RHFSelectField name="isconversationCompleted" variant="filled" width="100%" label="Conversation Completed" options={[{ label: 'All', value: '' }, ...yes_no.map((op) => ({ label: _.startCase(op), value: op }))]} />
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <LoadingButton loading={downloading} onClick={handleDownload} variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}>
                                    Download
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Grid>

                <Grid item xs={12} sx={{
                    '& .row-conversation-completed': {
                        backgroundColor: '#e8f5e9',
                    },
                }}>
                    <ProzoDataGrid
                        columns={columnsItemModal}
                        rows={groupedByAwbNumber || []}
                        page={page}
                        setPage={setPage}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        hideFooterPagination={false}
                        rowCount={totalSent}
                        getRowId={(row) => row?.id}
                        autoHeight={true}
                        pagination={true}
                        getRowClassName={(params) =>
                            params.row?.isconversationCompleted ? 'row-conversation-completed' : ''
                        }
                        paginationMode="server"
                    />
                </Grid>
            </Grid>
            {conversation &&
                <WhatsAppChatPopup conversation={conversation} setConversation={setConversation} />
            }
        </React.Fragment>
    )
}
